import {
  CPIManifestType,
  CPIManifestUIArea,
  CPIManifestUIDateRange,
  CPIManifestUIFrequency,
  CPIManifestUIUserInput,
  ManifestRecordView
} from './Manifest'

export enum IDENTITY_COLUMN_NAME {
  ONYXIA_PRODUCT_NAME = 'ONYXIA_PRODUCT_NAME',
  ONYXIA_PRODUCT_INSTANCE = 'ONYXIA_PRODUCT_INSTANCE',
  USER_ID = 'USER_ID',
  EMAIL_ADDRESSES = 'EMAIL_ADDRESSES',
  EMAILS_ADDRESSES = 'EMAILS_ADDRESSES',
  FIRST_NAME = 'FIRST_NAME',
  LAST_NAME = 'LAST_NAME',
  LOCATION = 'LOCATION',
  GROUP_NAMES = 'GROUP_NAMES',
  DEPARTMENTS = 'DEPARTMENTS',
  IS_HUMAN = 'IS_HUMAN',
  USER_STATUS = 'USER_STATUS',
  USER_ROLES = 'USER_ROLES',
  ADMIN_STATUS = 'ADMIN_STATUS',
  LAST_LOGIN = 'LAST_LOGIN',
  PASSWORD_LAST_UPDATED = 'PASSWORD_LAST_UPDATED',
  IS_BYPASS = 'IS_BYPASS',
  HAS_MFA = 'HAS_MFA',
  IS_STRONG = 'IS_STRONG'
}

export enum DEVICES_COLUMN_NAME {
  UDID = 'UDID',
  ONYXIA_PRODUCT_NAME = 'ONYXIA_PRODUCT_NAME',
  ONYXIA_PRODUCT_INSTANCE = 'ONYXIA_PRODUCT_INSTANCE',
  ONYXIA_TENANT_SLUG = 'ONYXIA_TENANT_SLUG',
  HOSTNAME = 'HOSTNAME',
  SERIAL_NUMBER = 'SERIAL_NUMBER',
  ASSET_TYPE = 'ASSET_TYPE',
  USER_ID = 'USER_ID',
  EMAIL_ADDRESSES = 'EMAIL_ADDRESSES',
  USER_NAME = 'USER_NAME',
  GROUP_NAME = 'GROUP_NAME',
  GROUP_IDS = 'GROUP_IDS',
  FIRST_SEEN = 'FIRST_SEEN',
  LAST_SEEN = 'LAST_SEEN',
  MAC_ADDRESSES = 'MAC_ADDRESSES',
  IP_ADDRESSES = 'IP_ADDRESSES',
  OS_NAME = 'OS_NAME',
  OS_VERSION = 'OS_VERSION',
  ENCRYPTION_STATUS = 'ENCRYPTION_STATUS',
  MANAGEMENT_STATUS = 'MANAGEMENT_STATUS',
  COMPLIANCE_STATUS = 'COMPLIANCE_STATUS',
  SUPERVISION_STATUS = 'SUPERVISION_STATUS',
  ENTITY_URL = 'ENTITY_URL',
  APPLE_MANAGEMENT_STATUS = 'APPLE_MANAGEMENT_STATUS',
  IS_ENCRYPTED = 'IS_ENCRYPTED',
  IS_COMPLIANCE = 'IS_COMPLIANCE',
  IS_SUPERVISED = 'IS_SUPERVISED',
  IS_MANAGED = 'IS_MANAGED',
  IS_APPLE = 'IS_APPLE',
  HAS_CLOUD_BACKUP = 'HAS_CLOUD_BACKUP',
  RISK_SCORE = 'RISK_SCORE',
  IS_INACTIVE = 'IS_INACTIVE',
  ASSET_ID = 'ASSET_ID',
  EMAIL_ADDRESS = 'EMAIL_ADDRESS',
  USER_NAMES = 'USER_NAMES',
  GROUP_NAMES = 'GROUP_NAMES'
}

export interface DevicesColumn {
  name: string
  displayName?: string
  type: string
  active: boolean
  filterPredicates?: string[] | null
}

export type ALL_COLUMN_NAMES = DEVICES_COLUMN_NAME | IDENTITY_COLUMN_NAME

export interface TrendBatchInputSchema {
  dataType: string
  name: string
  displayName: string | null
  displayByDefault: boolean
  filterPredicates: string[] | null
}

export interface TrendBatchMappings {
  columnName: string
  expression: string
}

export interface TrendBatchSpec {
  input_schema: TrendBatchInputSchema[]
  mappings: TrendBatchMappings[]
}

export interface RecordBatchInputSchemaItem {
  dataType: string
  name: string
  displayName: string
  displayByDefault: boolean
  filterPredicates: string[] | null
  exclusionRecordIdentifier?: boolean
}

export interface RecordBatchSpec {
  input_schema: RecordBatchInputSchemaItem[]
  mappings: TrendBatchMappings[]
}

export interface MetricManifest {
  id: string
  created_at: string
  updated_at: string
  slug: string
  display_name: string
  title: string
  description: string
  spec: {
    long_description: string
    business_value: string
    formula: {
      url: string
    }
    cybersecurity_frameworks: string[]
    weekly_calculation: string
    predictions: {
      is_supported: boolean
    }
    ui: {
      record_view?: ManifestRecordView
      trend_chart: {
        user_input: CPIManifestUIUserInput
        date_range: CPIManifestUIDateRange
        frequency: CPIManifestUIFrequency
        areas: CPIManifestUIArea[]
        type?: CPIManifestType
      }
    }
  }
  druid_spec: {
    record_batch_spec: RecordBatchSpec
    trend_batch_spec: TrendBatchSpec
  }
  workflow_spec: {
    run_on_sla_change: boolean
  }
  supported_categories: string[]
  supported_products: string[]
  supported_models: string[]
  supported_frameworks: string[]
  version: string
}

export interface ModelManifest {
  slug: string
  spec: {
    columns: DevicesColumn[]
  }
  supported_products: Array<string>
  id: string
  updated_at: string
  description: string
  title: string
  druid_spec?: {
    data_schema: {
      dimensions_spec: {
        dimensions: Array<string | { type: string; name: string }>
      }
    }
    transform_spec: {
      transforms: [
        {
          type: string
          name: string
          expression: string
        },
        {
          type: string
          name: string
          expression: string
        },
        {
          type: string
          name: string
          expression: string
        },
        {
          type: string
          name: string
          expression: string
        },
        {
          type: string
          name: string
          expression: string
        }
      ]
    }
  }
  supported_metrics: Array<string>
  created_at: string
  eligible_exclusion_fields?: import('./ExclusionRule').EligibleExclusionField[]
  units?: string
}

export interface DevicesDataItem {
  [DEVICES_COLUMN_NAME.ASSET_TYPE]: string | null
  [DEVICES_COLUMN_NAME.EMAIL_ADDRESS]: string | null
  [DEVICES_COLUMN_NAME.EMAIL_ADDRESSES]: string | null
  [DEVICES_COLUMN_NAME.ENTITY_URL]: string | null
  [DEVICES_COLUMN_NAME.FIRST_SEEN]: string | null
  [DEVICES_COLUMN_NAME.GROUP_IDS]: string | null
  [DEVICES_COLUMN_NAME.GROUP_NAME]: string | null
  [DEVICES_COLUMN_NAME.GROUP_NAMES]: string | null
  [DEVICES_COLUMN_NAME.HOSTNAME]: string | null
  [DEVICES_COLUMN_NAME.IP_ADDRESSES]: string | null
  [DEVICES_COLUMN_NAME.LAST_SEEN]: string | null
  [DEVICES_COLUMN_NAME.MAC_ADDRESSES]: string | null
  [DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_INSTANCE]: string | null
  [DEVICES_COLUMN_NAME.ONYXIA_PRODUCT_NAME]: string | null
  [DEVICES_COLUMN_NAME.ONYXIA_TENANT_SLUG]: string | null
  [DEVICES_COLUMN_NAME.OS_NAME]: string | null
  [DEVICES_COLUMN_NAME.OS_VERSION]: string | null
  [DEVICES_COLUMN_NAME.SERIAL_NUMBER]: string | null
  [DEVICES_COLUMN_NAME.UDID]: string | null
  [DEVICES_COLUMN_NAME.USER_ID]: string | null
  [DEVICES_COLUMN_NAME.USER_NAME]: string | null
  [DEVICES_COLUMN_NAME.USER_NAMES]: string | null
  [DEVICES_COLUMN_NAME.COMPLIANCE_STATUS]: boolean | null
  [DEVICES_COLUMN_NAME.ENCRYPTION_STATUS]: boolean | null
  [DEVICES_COLUMN_NAME.MANAGEMENT_STATUS]: boolean | null
  [DEVICES_COLUMN_NAME.SUPERVISION_STATUS]: boolean | null
  [DEVICES_COLUMN_NAME.APPLE_MANAGEMENT_STATUS]: boolean | null
  [DEVICES_COLUMN_NAME.IS_ENCRYPTED]: boolean | null
  [DEVICES_COLUMN_NAME.IS_COMPLIANCE]: boolean | null
  [DEVICES_COLUMN_NAME.IS_SUPERVISED]: boolean | null
  [DEVICES_COLUMN_NAME.IS_MANAGED]: boolean | null
  [DEVICES_COLUMN_NAME.HAS_CLOUD_BACKUP]: boolean | null
  [DEVICES_COLUMN_NAME.RISK_SCORE]: string | null
  [DEVICES_COLUMN_NAME.IS_APPLE]: string | null
  [DEVICES_COLUMN_NAME.IS_INACTIVE]: boolean | null
  [DEVICES_COLUMN_NAME.ASSET_ID]: string | null
  __time: string
}

export interface DevicesQueryPagination {
  current_page: number
  next_page: number | null
  previous_page: number | null
  total_results: number
}

export interface DevicesQueryResponse {
  data: DevicesDataItem[]
  index_name: string
  pagination: DevicesQueryPagination
}

export type DevicesQueryPayload = {
  [k in QueryPayloadKey]?: {
    [predicate: string]: string | string[] | boolean | number | Date
  }
}

export type QueryPayloadKey = DEVICES_COLUMN_NAME | IDENTITY_COLUMN_NAME | string

export type GenericQueryPayload = Record<string, Record<string, string | string[] | boolean | number | Date>>

export interface ColumnValue {
  columnName: ALL_COLUMN_NAMES
  value: string[]
}
